/**
 * Applies the CalendarDate brand at a validated boundary.
 *
 * The double assertion goes through a generic so this file contains no literal
 * `as CalendarDate` token — the GritQL guard forbids that cast everywhere, and
 * plugin diagnostics cannot be suppressed per-line, so the sanctioned mint site
 * uses this helper instead. Callers must pass an already-validated "YYYY-MM-DD".
 */
function brand(value) {
    return value;
}
export class InvalidCalendarDateError extends Error {
    input;
    field;
    constructor(input, field) {
        super(`Invalid calendar date for "${field}": ${JSON.stringify(input)}`);
        this.input = input;
        this.field = field;
        this.name = "InvalidCalendarDateError";
    }
}
// ---------------------------------------------------------------------------
// Gregorian kernel — dependency-free, validated y/m/d in, y/m/d out.
// Serial arithmetic uses Howard Hinnant's days-from-civil algorithm, valid for
// the proleptic Gregorian calendar. All arithmetic below builds on the kernel,
// so there is exactly one implementation of day-counting and month clamping.
// ---------------------------------------------------------------------------
const MIN_YEAR = 1;
const MAX_YEAR = 9999;
function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
/** Number of days in month `m` (1–12) of `year`. */
function daysInMonth(year, m) {
    if (m === 2) {
        return isLeapYear(year) ? 29 : 28;
    }
    const lengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return lengths[m - 1];
}
/** Real-calendar validity: rejects 2026-02-30, 2026-13-01, etc. */
function isValidYmd(year, month, day) {
    if (!Number.isInteger(year) ||
        !Number.isInteger(month) ||
        !Number.isInteger(day)) {
        return false;
    }
    if (year < MIN_YEAR || year > MAX_YEAR) {
        return false;
    }
    if (month < 1 || month > 12) {
        return false;
    }
    return day >= 1 && day <= daysInMonth(year, month);
}
/** Days since 1970-01-01 for a valid `y/m/d` (month 1–12). */
function daysFromCivil(year, month, day) {
    const y = month <= 2 ? year - 1 : year;
    const era = Math.floor((y >= 0 ? y : y - 399) / 400);
    const yoe = y - era * 400;
    const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
    const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
    return era * 146097 + doe - 719468;
}
/** Inverse of `daysFromCivil`: a serial back to `y/m/d` (month 1–12). */
function civilFromDays(serial) {
    const z = serial + 719468;
    const era = Math.floor((z >= 0 ? z : z - 146096) / 146097);
    const doe = z - era * 146097;
    const yoe = Math.floor((doe -
        Math.floor(doe / 1460) +
        Math.floor(doe / 36524) -
        Math.floor(doe / 146096)) /
        365);
    const year = yoe + era * 400;
    const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
    const mp = Math.floor((5 * doy + 2) / 153);
    const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
    const month = mp < 10 ? mp + 3 : mp - 9;
    return { year: month <= 2 ? year + 1 : year, month, day };
}
/** Weekday for a serial: 0 = Sunday … 6 = Saturday. 1970-01-01 was a Thursday. */
function weekdayFromDays(serial) {
    return ((((serial % 7) + 4) % 7) + 7) % 7;
}
/** Adds `months` to `y/m/d`, clamping the day into the target month. */
function addMonthsToYmd(year, month, day, months) {
    const total = year * 12 + (month - 1) + months;
    const normalizedMonth = ((total % 12) + 12) % 12;
    const targetYear = (total - normalizedMonth) / 12;
    const targetMonth = normalizedMonth + 1;
    const clampedDay = Math.min(day, daysInMonth(targetYear, targetMonth));
    return { year: targetYear, month: targetMonth, day: clampedDay };
}
const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function pad2(value) {
    return value < 10 ? `0${value}` : `${value}`;
}
function pad4(value) {
    return `${value}`.padStart(4, "0");
}
function formatYmd(year, month, day) {
    return `${pad4(year)}-${pad2(month)}-${pad2(day)}`;
}
/** Splits an already-branded CalendarDate into its numeric parts. */
function partsOf(date) {
    return {
        year: Number(date.slice(0, 4)),
        month: Number(date.slice(5, 7)),
        day: Number(date.slice(8, 10)),
    };
}
function serialOf(date) {
    const { year, month, day } = partsOf(date);
    return daysFromCivil(year, month, day);
}
// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------
/**
 * Total parser: the only sanctioned entry point for external strings.
 * Enforces the `YYYY-MM-DD` shape AND real-calendar validity.
 */
export function parseCalendarDate(input, field) {
    if (typeof input !== "string" || !CALENDAR_DATE_RE.test(input)) {
        throw new InvalidCalendarDateError(input, field);
    }
    const year = Number(input.slice(0, 4));
    const month = Number(input.slice(5, 7));
    const day = Number(input.slice(8, 10));
    if (!isValidYmd(year, month, day)) {
        throw new InvalidCalendarDateError(input, field);
    }
    return brand(input);
}
export function isCalendarDate(value) {
    if (typeof value !== "string" || !CALENDAR_DATE_RE.test(value)) {
        return false;
    }
    return isValidYmd(Number(value.slice(0, 4)), Number(value.slice(5, 7)), Number(value.slice(8, 10)));
}
export function calendarDateFromParts(year, month, day) {
    if (!isValidYmd(year, month, day)) {
        throw new InvalidCalendarDateError(`${year}-${month}-${day}`, "calendarDateFromParts");
    }
    return brand(formatYmd(year, month, day));
}
/**
 * The pure, testable core: which calendar day a given instant falls on, in a
 * named zone. Uses Intl formatting so it is zone-correct and constructs no local
 * `Date` — which is what makes it deterministic for a fixed instant.
 */
export function calendarDateInTimeZone(instant, timeZone) {
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(instant);
    let year = 0;
    let month = 0;
    let day = 0;
    for (const part of parts) {
        if (part.type === "year") {
            year = Number(part.value);
        }
        else if (part.type === "month") {
            month = Number(part.value);
        }
        else if (part.type === "day") {
            day = Number(part.value);
        }
    }
    return calendarDateFromParts(year, month, day);
}
/**
 * Thin composition-edge wrapper over `calendarDateInTimeZone`. Reads the process
 * clock; the required `timeZone` argument removes the ambient zone (not the
 * ambient clock), so every "which day is it?" decision stays greppable.
 */
export function todayIn(timeZone) {
    return calendarDateInTimeZone(Date.now(), timeZone);
}
export function addCalendarDays(date, days) {
    const { year, month, day } = civilFromDays(serialOf(date) + days);
    return brand(formatYmd(year, month, day));
}
/** Adds calendar months, clamping day-of-month (Jan 31 +1mo -> Feb 28/29). */
export function addCalendarMonths(date, months) {
    const current = partsOf(date);
    const next = addMonthsToYmd(current.year, current.month, current.day, months);
    return brand(formatYmd(next.year, next.month, next.day));
}
export function differenceInCalendarDays(from, to) {
    return serialOf(to) - serialOf(from);
}
export function compareCalendarDates(a, b) {
    const diff = serialOf(a) - serialOf(b);
    if (diff < 0) {
        return -1;
    }
    return diff > 0 ? 1 : 0;
}
/** Day of week, zone-free. 0 = Sunday … 6 = Saturday. */
export function dayOfWeekOf(date) {
    return weekdayFromDays(serialOf(date));
}
// ---------------------------------------------------------------------------
// Kernel — consumed inward by ./utils/recurringCalendar.ts so there is a single
// clamping/day-count implementation across this package. Exported so siblings can
// import it directly; it is a low-level utility, not the CalendarDate surface.
// ---------------------------------------------------------------------------
export const gregorianKernel = {
    isLeapYear,
    daysInMonth,
    isValidYmd,
    daysFromCivil,
    civilFromDays,
    weekdayFromDays,
    addMonthsToYmd,
};
//# sourceMappingURL=CalendarDate.js.map